import React, { useState, useRef, useEffect } from 'react';
import { cn } from '../../lib/utils';

/**
 * TimeField - Custom 24-hour time input component for STAGE8
 * 
 * @param {string} label - Label text for the field
 * @param {string} value - Time value in "HH:MM" format
 * @param {function} onChange - Callback when time changes, receives "HH:MM" string
 * @param {string} pairedValue - Optional paired time for duration calculation
 * @param {string} pairedLabel - Label for the paired field (e.g., "Start" to show duration from Start)
 * @param {boolean} showDuration - Whether to show duration calculation
 * @param {string} className - Additional CSS classes
 * @param {string} testId - data-testid for testing
 */
export function TimeField({
  label,
  value = '',
  onChange,
  pairedValue,
  pairedLabel,
  showDuration = false,
  className,
  testId,
}) {
  // Parse initial value
  const parseTime = (timeStr) => {
    if (!timeStr || typeof timeStr !== 'string') return { hours: '', minutes: '' };
    const parts = timeStr.split(':');
    return {
      hours: parts[0] || '',
      minutes: parts[1] || '',
    };
  };

  const [hours, setHours] = useState(() => parseTime(value).hours);
  const [minutes, setMinutes] = useState(() => parseTime(value).minutes);
  const [isFocused, setIsFocused] = useState(false);
  const [hasError, setHasError] = useState(false);
  
  const hoursRef = useRef(null);
  const minutesRef = useRef(null);

  // Sync with external value changes
  useEffect(() => {
    const parsed = parseTime(value);
    setHours(parsed.hours);
    setMinutes(parsed.minutes);
  }, [value]);

  // Clamp value within range
  const clamp = (val, min, max) => {
    const num = parseInt(val, 10);
    if (isNaN(num)) return min;
    return Math.min(Math.max(num, min), max);
  };

  // Pad single digit with leading zero
  const pad = (val) => {
    if (val === '') return '';
    const num = parseInt(val, 10);
    if (isNaN(num)) return '00';
    return num.toString().padStart(2, '0');
  };

  // Emit change to parent
  const emitChange = (h, m) => {
    const paddedH = pad(h) || '00';
    const paddedM = pad(m) || '00';
    const timeStr = `${paddedH}:${paddedM}`;
    onChange?.(timeStr);
  };

  // Handle hours input
  const handleHoursChange = (e) => {
    let val = e.target.value.replace(/\D/g, ''); // Only digits
    
    if (val.length > 2) {
      val = val.slice(0, 2);
    }

    // Clamp to valid range
    if (val.length === 2) {
      const clamped = clamp(val, 0, 23);
      val = clamped.toString().padStart(2, '0');
      setHours(val);
      setHasError(false);
      // Auto-advance to minutes
      minutesRef.current?.focus();
      minutesRef.current?.select();
      emitChange(val, minutes);
    } else {
      setHours(val);
      // Check if first digit is > 2, clamp immediately
      if (val.length === 1 && parseInt(val, 10) > 2) {
        const clamped = '0' + val;
        setHours(clamped);
        minutesRef.current?.focus();
        minutesRef.current?.select();
        emitChange(clamped, minutes);
      }
    }
  };

  // Handle minutes input
  const handleMinutesChange = (e) => {
    let val = e.target.value.replace(/\D/g, ''); // Only digits
    
    if (val.length > 2) {
      val = val.slice(0, 2);
    }

    // Clamp to valid range
    if (val.length === 2) {
      const clamped = clamp(val, 0, 59);
      val = clamped.toString().padStart(2, '0');
      setHasError(false);
    }

    setMinutes(val);
    if (val.length === 2) {
      emitChange(hours, val);
    }
  };

  // Handle blur - finalize values
  const handleHoursBlur = () => {
    if (hours !== '') {
      const clamped = clamp(hours, 0, 23);
      const padded = pad(clamped);
      setHours(padded);
      emitChange(padded, minutes);
    }
    checkFocus();
  };

  const handleMinutesBlur = () => {
    if (minutes !== '') {
      const clamped = clamp(minutes, 0, 59);
      const padded = pad(clamped);
      setMinutes(padded);
      emitChange(hours, padded);
    }
    checkFocus();
  };

  const checkFocus = () => {
    setTimeout(() => {
      const active = document.activeElement;
      if (active !== hoursRef.current && active !== minutesRef.current) {
        setIsFocused(false);
      }
    }, 10);
  };

  // Handle arrow keys and increment/decrement
  const handleKeyDown = (e, type) => {
    const isHours = type === 'hours';
    const currentVal = parseInt(isHours ? hours : minutes, 10) || 0;
    const max = isHours ? 23 : 59;
    
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      const newVal = currentVal >= max ? 0 : currentVal + 1;
      const padded = pad(newVal);
      if (isHours) {
        setHours(padded);
        emitChange(padded, minutes);
      } else {
        setMinutes(padded);
        emitChange(hours, padded);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const newVal = currentVal <= 0 ? max : currentVal - 1;
      const padded = pad(newVal);
      if (isHours) {
        setHours(padded);
        emitChange(padded, minutes);
      } else {
        setMinutes(padded);
        emitChange(hours, padded);
      }
    } else if (e.key === 'Backspace' && !isHours && minutes === '' && hours !== '') {
      // Jump back to hours on backspace when minutes is empty
      hoursRef.current?.focus();
    }
  };

  // Handle mouse wheel
  const handleWheel = (e, type) => {
    e.preventDefault();
    const isHours = type === 'hours';
    const currentVal = parseInt(isHours ? hours : minutes, 10) || 0;
    const max = isHours ? 23 : 59;
    const delta = e.deltaY < 0 ? 1 : -1;
    
    let newVal = currentVal + delta;
    if (newVal > max) newVal = 0;
    if (newVal < 0) newVal = max;
    
    const padded = pad(newVal);
    if (isHours) {
      setHours(padded);
      emitChange(padded, minutes);
    } else {
      setMinutes(padded);
      emitChange(hours, padded);
    }
  };

  // Quick time adjustment
  const adjustTime = (addMinutes) => {
    const currentHours = parseInt(hours, 10) || 0;
    const currentMinutes = parseInt(minutes, 10) || 0;
    
    let totalMinutes = currentHours * 60 + currentMinutes + addMinutes;
    
    // Handle overflow/underflow within 24 hours
    if (totalMinutes < 0) totalMinutes = 0;
    if (totalMinutes >= 24 * 60) totalMinutes = 24 * 60 - 1;
    
    const newHours = Math.floor(totalMinutes / 60);
    const newMinutes = totalMinutes % 60;
    
    const paddedH = pad(newHours);
    const paddedM = pad(newMinutes);
    
    setHours(paddedH);
    setMinutes(paddedM);
    emitChange(paddedH, paddedM);
  };

  // Calculate duration between two times
  const calculateDuration = () => {
    if (!showDuration || !pairedValue) return null;
    
    const currentH = parseInt(hours, 10);
    const currentM = parseInt(minutes, 10);
    const pairedParts = pairedValue.split(':');
    const pairedH = parseInt(pairedParts[0], 10);
    const pairedM = parseInt(pairedParts[1], 10);
    
    if (isNaN(currentH) || isNaN(currentM) || isNaN(pairedH) || isNaN(pairedM)) {
      return null;
    }
    
    const currentTotal = currentH * 60 + currentM;
    const pairedTotal = pairedH * 60 + pairedM;
    
    // Calculate difference (assuming this field is "End" and paired is "Start")
    let diff = currentTotal - pairedTotal;
    
    // Handle overnight (e.g., 23:00 to 02:00)
    if (diff < 0) {
      diff += 24 * 60;
    }
    
    if (diff === 0) return null;
    
    const durationH = Math.floor(diff / 60);
    const durationM = diff % 60;
    
    if (durationH > 0 && durationM > 0) {
      return `${durationH}h ${durationM}m`;
    } else if (durationH > 0) {
      return `${durationH}h`;
    } else {
      return `${durationM}m`;
    }
  };

  const duration = calculateDuration();

  return (
    <div className={cn('flex flex-col', className)}>
      {label && (
        <label className="text-[11px] uppercase tracking-wider text-[var(--t2,#666)] font-medium mb-1.5">
          {label}
        </label>
      )}
      
      <div
        className={cn(
          'inline-flex items-center gap-0.5 rounded-md px-2 py-2 transition-all duration-150',
          'bg-[var(--s2,#1a1a1a)] border',
          isFocused 
            ? 'border-[var(--amber,var(--s8-amber))] shadow-[0_0_0_1px_rgba(200,155,90,0.15)]' 
            : 'border-[var(--b2,#2a2a2a)]',
          hasError && 'border-[var(--red,#ef4444)]'
        )}
        data-testid={testId}
      >
        {/* Hours Input */}
        <input
          ref={hoursRef}
          type="text"
          inputMode="numeric"
          value={hours}
          onChange={handleHoursChange}
          onFocus={() => {
            setIsFocused(true);
            hoursRef.current?.select();
          }}
          onBlur={handleHoursBlur}
          onKeyDown={(e) => handleKeyDown(e, 'hours')}
          onWheel={(e) => handleWheel(e, 'hours')}
          placeholder="00"
          maxLength={2}
          className={cn(
            'w-8 text-center bg-transparent outline-none',
            'font-mono text-sm tracking-wider',
            'text-[var(--t0,#fff)] placeholder:text-[var(--t3,#444)]'
          )}
          style={{ fontFamily: "'DM Mono', monospace" }}
          data-testid={testId ? `${testId}-hours` : undefined}
        />
        
        {/* Colon Separator */}
        <span 
          className="text-[var(--t2,#666)] font-mono text-sm select-none"
          style={{ fontFamily: "'DM Mono', monospace" }}
        >
          :
        </span>
        
        {/* Minutes Input */}
        <input
          ref={minutesRef}
          type="text"
          inputMode="numeric"
          value={minutes}
          onChange={handleMinutesChange}
          onFocus={() => {
            setIsFocused(true);
            minutesRef.current?.select();
          }}
          onBlur={handleMinutesBlur}
          onKeyDown={(e) => handleKeyDown(e, 'minutes')}
          onWheel={(e) => handleWheel(e, 'minutes')}
          placeholder="00"
          maxLength={2}
          className={cn(
            'w-8 text-center bg-transparent outline-none',
            'font-mono text-sm tracking-wider',
            'text-[var(--t0,#fff)] placeholder:text-[var(--t3,#444)]'
          )}
          style={{ fontFamily: "'DM Mono', monospace" }}
          data-testid={testId ? `${testId}-minutes` : undefined}
        />
      </div>
      
      {/* Quick Controls */}
      <div className="flex items-center gap-1 mt-1.5">
        {[15, 30, 60].map((mins) => (
          <button
            key={mins}
            type="button"
            onClick={() => adjustTime(mins)}
            className={cn(
              'px-1.5 py-0.5 text-[10px] font-mono rounded transition-colors',
              'bg-[var(--s3,#222)] border border-[var(--b2,#2a2a2a)]',
              'text-[var(--t2,#888)] hover:text-[var(--t0,#fff)]',
              'hover:border-[var(--amber,var(--s8-amber))] hover:bg-[var(--s2,#1a1a1a)]'
            )}
            style={{ fontFamily: "'DM Mono', monospace" }}
          >
            +{mins === 60 ? '1h' : `${mins}m`}
          </button>
        ))}
      </div>
      
      {/* Duration Display */}
      {duration && (
        <div className="mt-1.5 text-[10px] font-mono text-[var(--amber,var(--s8-amber))]" style={{ fontFamily: "'DM Mono', monospace" }}>
          Duration: {duration}
        </div>
      )}
    </div>
  );
}

/**
 * TimeFieldGroup - Horizontal group of time fields
 */
export function TimeFieldGroup({ children, className }) {
  return (
    <div className={cn(
      'flex flex-wrap gap-4 md:gap-6',
      className
    )}>
      {children}
    </div>
  );
}

export default TimeField;
